import { useCallback, useEffect, useState } from "react";

/**
 * Camada de LINGUAGEM SIMPLES da interface (Prompt 44).
 *
 * Regra: o cliente comum nunca lê termo técnico. "Executar migration" vira
 * "Atualizando o banco de dados", "commit" vira "Salvando uma versão".
 * Nada aqui muda arquitetura: é só apresentação. O texto técnico original
 * continua disponível para quem liga o MODO AVANÇADO.
 */

const ADVANCED_KEY = "okton.advanced-mode";
const ADVANCED_EVENT = "okton:advanced-mode";

export function isAdvancedMode(): boolean {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(ADVANCED_KEY) === "1";
}

/** Modo avançado: mostra detalhes técnicos (arquivos, diff, logs, banco). */
export function useAdvancedMode(): [boolean, (value: boolean) => void] {
  const [advanced, setAdvanced] = useState<boolean>(isAdvancedMode);

  useEffect(() => {
    const sync = () => setAdvanced(isAdvancedMode());
    window.addEventListener(ADVANCED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(ADVANCED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const update = useCallback((value: boolean) => {
    window.localStorage.setItem(ADVANCED_KEY, value ? "1" : "0");
    setAdvanced(value);
    window.dispatchEvent(new Event(ADVANCED_EVENT));
  }, []);

  return [advanced, update];
}

/** Etapas do fluxo principal, na ordem em que o usuário as vive. */
export const JOURNEY = [
  "Criar projeto",
  "Descrever a ideia",
  "A IA planeja",
  "Sistema é criado",
  "Ver funcionando",
  "Pedir alterações",
  "Publicar",
] as const;

/** Traduções por tipo de etapa vindo da execução. */
const BY_TYPE: Record<string, string> = {
  analyze: "Entendendo o seu sistema",
  plan: "Planejando o que fazer",
  create_file: "Criando as telas e as regras",
  edit_file: "Ajustando as telas e as regras",
  database: "Atualizando o banco de dados",
  migration: "Atualizando o banco de dados",
  build: "Testando o sistema",
  test: "Testando o sistema",
  security: "Conferindo a segurança",
  commit: "Salvando uma nova versão",
  preview: "Preparando a visualização",
  docs: "Atualizando a documentação",
  memory: "Guardando o histórico do projeto",
};

/** Traduções por trecho de texto (o texto cru vem do servidor). */
const BY_TEXT: Array<[RegExp, string]> = [
  [/montagem de contexto|mapa de depend/i, "Entendendo o seu sistema"],
  [/plano|planner/i, "Planejando o que fazer"],
  [/migration|migra(ç|c)/i, "Atualizando o banco de dados"],
  [/banco de dados/i, "Atualizando o banco de dados"],
  [/build|teste|verifica/i, "Testando o sistema"],
  [/segurança/i, "Conferindo a segurança"],
  [/checkpoint/i, "Guardando um ponto de retorno"],
  [/commit|vers(ã|a)o/i, "Salvando uma nova versão"],
  [/preview/i, "Preparando a visualização"],
  [/arquivo/i, "Ajustando o sistema"],
];

/**
 * Texto de etapa em linguagem do cliente. Em modo avançado devolve o texto
 * original, que traz o detalhe técnico.
 */
export function plainStep(
  step: { type?: string; label?: string; title?: string },
  advanced = false,
): string {
  const original = step.label || step.title || "";
  if (advanced) return original;

  const byType = step.type ? BY_TYPE[step.type] : undefined;
  if (byType) return byType;

  for (const [pattern, text] of BY_TEXT) {
    if (pattern.test(original)) return text;
  }

  return original || "Trabalhando no seu sistema";
}

/** Resumo do que mudou, sem jargão. */
export function plainSummary(summary: {
  files_created?: number;
  files_modified?: number;
  migrations_created?: number;
  build?: string;
  tests_passed?: boolean;
  failed_stage?: string | null;
}): string[] {
  const touched = (summary.files_created ?? 0) + (summary.files_modified ?? 0);
  const lines: string[] = [];

  lines.push(
    touched === 0
      ? "Nada precisou ser alterado."
      : `${touched} parte(s) do sistema foram atualizadas.`,
  );

  if ((summary.migrations_created ?? 0) > 0) {
    lines.push("O banco de dados foi atualizado.");
  }

  lines.push(
    summary.build === "ok"
      ? "Tudo foi testado e está funcionando."
      : summary.build === "failed"
        ? "Alguma verificação não passou — você pode pedir uma nova tentativa."
        : "As verificações ainda não foram concluídas.",
  );

  if (summary.tests_passed === false) {
    lines.push("Ficaram pendências nos testes automáticos.");
  }

  return lines;
}

/** Resumo técnico completo (modo avançado). */
export function technicalSummary(summary: {
  files_created?: number;
  files_modified?: number;
  migrations_created?: number;
  build?: string;
  tests_passed?: boolean;
  failed_stage?: string | null;
  attempts?: number;
}): string[] {
  return [
    `${summary.files_modified ?? 0} arquivo(s) modificado(s)`,
    `${summary.files_created ?? 0} arquivo(s) criado(s)`,
    `${summary.migrations_created ?? 0} migration(s)`,
    `Build: ${summary.build ?? "pendente"}${
      summary.failed_stage ? ` (falhou em ${summary.failed_stage})` : ""
    }`,
    `Testes: ${summary.tests_passed === false ? "com falhas" : "ok"}`,
    `Tentativas: ${summary.attempts ?? 1}`,
  ];
}

/** Estado do sistema em linguagem do cliente. */
const STATUS: Record<string, string> = {
  draft: "rascunho",
  queued: "na fila",
  building: "sendo criado",
  ready: "pronto",
  active: "ativo",
  running: "no ar",
  succeeded: "concluído",
  ok: "tudo certo",
  failed: "com problema",
  error: "com problema",
  stopped: "desligado",
  archived: "arquivado",
};

export function plainStatus(status: string, advanced = false): string {
  if (advanced) return status;
  return STATUS[status] ?? status;
}
